import {liveSession} from "../../../Engine/LiveSessionManagement.js";
import {store} from "../../../Store/index.js";
import type {DoorwayOverlay} from "../../../UI/Engine/Journey/Overlays/DoorwayOverlay.js";
import {nativeBridge} from "../Bridge_Native.js";
import {AudioFileEntry} from "./MediaTypes.js";
import {GeneralComp} from "../../../Engine/LiveSession/Components/GeneralComp.js";
import {PlaySoundResult, WebAudio_ForcedMute_Set, WebAudio_ForcedPan_Set, WebAudioPlayer_Opts, webAudioPlayerPool_noSession} from "../../Media/WebAudioPlayer.js";
import {SoundMutingType, SoundPanningType} from "../../../Server/DB/@Shared/EffectPointer.js";

export let DoorwayOverlay_current: DoorwayOverlay|n;
export function SetDoorwayOverlayCurrent(newInstance: DoorwayOverlay|n) {
	DoorwayOverlay_current = newInstance;
}

export async function PlaySound(extraOpt: {
	entry: AudioFileEntry,
	// temp
	allowDoorwayOverlayEffects?: boolean|n,
}, coreOpt: PlaySoundOpts_Init): Promise<PlaySoundResult> {
	const allowDoorwayOverlayEffects = extraOpt.allowDoorwayOverlayEffects ?? true;
	if (allowDoorwayOverlayEffects && DoorwayOverlay_current) {
		// TEMP; if playing sound of door opening/closing, do that effect on the doorway-overlay as well
		if (extraOpt.entry.filePath.ContainsAny("_Open", "_Close")) {
			DoorwayOverlay_current.SetDoorOpen(extraOpt.entry.filePath.Contains("_Open"), extraOpt.entry.duration);
		}
		// TEMP; if playing sound of police siren, do a flashing-lights effect on the doorway-overlay as well
		if (extraOpt.entry.filePath.Contains("/RCEffects")) {
			DoorwayOverlay_current.StartFlashing(extraOpt.entry.duration, store.main.engine.journey.flashConfig);
		}
	}

	return await PlaySound_ByContentUri({contentUri: extraOpt.entry.contentUri}, coreOpt);
}

// Why are all these fields non-nullable? Because we want any "fallback values" to be specified on this JS side, rather than in the native code. (JS faster to update)
type PlaySoundOpts_Nullable = {
	[K in keyof PlaySoundOpts]: PlaySoundOpts[K]|n;
};
type PlaySoundOpts_Init = ConstructorParameters<typeof PlaySoundOpts>[0];
export class PlaySoundOpts {
	constructor(data_nullable: Partial<PlaySoundOpts_Nullable>) {
		const d = data_nullable;
		const data_resolved: Omit<PlaySoundOpts, "Finalized"> = {
			speed_multiplier_min: d.speed_multiplier_min ?? 0,
			speed_multiplier_max: d.speed_multiplier_max ?? 0,
			speed_duration: d.speed_duration ?? 0,
			startTime_min: d.startTime_min ?? 0,
			startTime_max: d.startTime_max ?? 0,
			durationLimit: d.durationLimit ?? -1,
			loop: d.loop ?? false,

			playerId: d.playerId, // can be null
			panningType: d.panningType ?? undefined,
			mutingType: d.mutingType ?? undefined,

			volume: d.volume ?? 1,
			volume_evolveInterval: d.volume_evolveInterval ?? undefined,
			volume_evolveValues: d.volume_evolveValues ?? undefined,
			volume_evolveOffset: d.volume_evolveOffset ?? undefined,
			effect_applyGlobalMultiplier: d.effect_applyGlobalMultiplier ?? true,
			effect_applyLoudnessCurve: d.effect_applyLoudnessCurve ?? true,
		};
		Object.assign(this, data_resolved);
	}
	/** For no effect, set min and max <=0. */
	speed_multiplier_min!: number;
	/** For no effect, set min and max <=0. */
	speed_multiplier_max!: number;
	/** In seconds. For no effect, set <=0. */
	speed_duration!: number;
	/** Start time within the file, in seconds. (min) */
	startTime_min!: number;
	/** Start time within the file, in seconds. (max) */
	startTime_max!: number;
	/** In seconds. For no effect (ie. no limit), set -1.*/
	durationLimit!: number;
	loop!: boolean;
	/** If left undefined here, ends up resolving to the global panning-type fallback. */
	panningType: SoundPanningType|n;
	/** If left undefined here, ends up resolving to the global muting-type fallback. */
	mutingType: SoundMutingType|n;

	/** Volume (0–1+). Note: Global volume effects get applied on-top-of/after this value (and volume_evolveValues), by default. */
	volume!: number;
	/** In seconds. Time between each volume evolve step. Only used when volume_evolveValues is set. */
	volume_evolveInterval: number|n;
	/** Volume replacement values (0–1+), applied as discrete steps at each interval tick during playback. (replace the base volume at each step) */
	volume_evolveValues: number[]|n;
	/** In seconds. If set, the volume evolution starts at this offset into the evolve sequence, picking up where a previous run left off. */
	volume_evolveOffset: number|n;

	/** If true (the default), the global volume multiplier is applied to volume and volume_evolveValues before playback. */
	effect_applyGlobalMultiplier!: boolean;
	/** If true (the default), the loudness curve is applied to volume and volume_evolveValues before playback. */
	effect_applyLoudnessCurve!: boolean;

	playerId?: string|n;

	Finalized() {
		const applyEffects = (vol: number)=>ApplyVolumeEffectsToVolumeValue(vol, this.effect_applyGlobalMultiplier, this.effect_applyLoudnessCurve);
		return {
			...this.ExcludeKeys("panningType", "mutingType", "volume", "volume_evolveValues"),
			panningType: this.panningType ?? liveSession?.c.general.globalPanningTypeFallback ?? SoundPanningType.none,
			mutingType: this.mutingType ?? liveSession?.c.general.globalMutingTypeFallback ?? SoundMutingType.none,
			volume_final: applyEffects(this.volume),
			volume_evolveValues_final: this.volume_evolveValues?.map(applyEffects),
		} as PlaySoundOpts_Finalized;
	}
}
export function ApplyVolumeEffectsToVolumeValue(volume: number, applyGlobalMultiplier = true, applyLoudnessCurve = true) {
	const globalMult = liveSession?.GetSettingValue(c=>c.general.globalVolumeMultiplier) ?? 1;
	let result = volume;
	if (applyGlobalMultiplier) result = result * globalMult;
	if (applyLoudnessCurve) result = Math.pow(result, store.main.settings.audio.loudnessCurve_mediaPlayer);
	return result;
}
export type PlaySoundOpts_Finalized = Omit<PlaySoundOpts, "Finalized" | "panningType" | "mutingType" | "volume" | "volume_evolveValues"> & {
	panningType: SoundPanningType;
	mutingType: SoundMutingType;
	volume_final: number,
	volume_evolveValues_final: number[]|n,
};

export async function PlaySound_ByContentUri(extraOpt: {contentUri: string}, coreOpt: PlaySoundOpts_Init): Promise<PlaySoundResult> {
	const coreOptResolved = new PlaySoundOpts(coreOpt);

	// if a stop-request was already made for this playerId, then cancel it before it's actual playback even began
	// (can be useful in some cases, since the StopSound function is synchronous, whereas this one is asynchronous)
	if (coreOpt.playerId && stopSoundRequests.has(coreOpt.playerId)) {
		StopSound(coreOpt.playerId);
		return {playerId: coreOpt.playerId, playDuration: 0};
	}

	//const playerId_final = (await nativeBridge.Call("PlaySound_ByContentUri", opt)) as string;
	//const playerId_final = (await nativeBridge.Call("PlaySound_ByContentUri_Exo", opt)) as string;
	const playerOpts: WebAudioPlayer_Opts = {
		contentUri: extraOpt.contentUri,
		playOpts: coreOptResolved.Finalized(),
	};
	const pool = liveSession?.Comp(GeneralComp)?.webAudioPlayerPool ?? webAudioPlayerPool_noSession;
	return await pool.PlaySound(playerOpts);
}

export const stopSoundRequests = new Set<string>();
export function StopSound(playerId: string): Promise<boolean> {
	stopSoundRequests.add(playerId);
	// Try both pools; the sound was started in one of them and we don't track which.
	const sessionPool = liveSession?.Comp(GeneralComp)?.webAudioPlayerPool;
	const foundInSession = sessionPool?.StopSound(playerId) ?? false;
	const foundInNoSession = webAudioPlayerPool_noSession.StopSound(playerId);
	//return nativeBridge.Call("StopSound", playerId) as Promise<boolean>;
	//return nativeBridge.Call("StopSound_Exo", playerId) as Promise<boolean>;
	return Promise.resolve(foundInSession || foundInNoSession);
}

// todo: clarify/simplify filePathSubstring<>targetSoundPathEnd distinction (filePathSubstring is at media-store querying stage, targetSoundPathEnd is custom filtering of results; but still a bit clunky/underspecified)
/*#*
 * NOTE: In almost all cases, you want to use PlaySound_ByContentUri instead. (using the functions in cache.ts for selecting an audio-file)
 * Note: If no audio-file was played, returns an empty string.
 *#/
/*export async function PlaySound_ByFolderOrPath(filePathSubstring: string, targetSoundPathEnd: string|n, volume: number, /** -1 = no limit *#/ durationLimit: number, loop: boolean) {
	const [playerId, playedAudio_filePath, playedAudio_contentUri] = (await nativeBridge.Call("PlaySound_ByFolderOrPath", filePathSubstring, targetSoundPathEnd, volume, durationLimit, loop)) as string;
	return playedAudio_filePath;
}*/

export const PlaySound_StopAll_extraListeners = [] as (()=>void)[];
export async function PlaySound_StopAll() {
	PlaySound_StopAll_extraListeners.forEach(a=>a());
	//await nativeBridge.Call_NoReturn("PlaySound_StopAll");
	//await nativeBridge.Call_NoReturn("PlaySound_StopAll_Exo");
	liveSession?.Comp(GeneralComp)?.webAudioPlayerPool.StopAll();
	webAudioPlayerPool_noSession.StopAll();
}

/*export function SetForcedPan(panDirection: "left" | "right" | null, panStrength: number) {
	//if (!InAndroid(0)) return;
	//await nativeBridge.Call("SetForcedPan", panDirection);
	//await nativeBridge.Call("SetForcedPan_Exo", panDirection, panStrength);
	SetWebAudioForcedPan(panDirection, panStrength);
}
export function SetForcedMute(muteState: boolean | null) {
	SetWebAudioForcedMute(muteState);
}*/

/**
 * Save audio data (ArrayBuffer) to a file in the media store
 * @param audioData The audio data as ArrayBuffer
 * @param relFilePath The relative file path (like "Alarms/LF/BakedTTS/...")
 */
export async function SaveAudioDataToFile(audioData: ArrayBuffer, relFilePath: string): Promise<void> {
	// Convert ArrayBuffer to base64 string for transmission to native code
	const uint8Array = new Uint8Array(audioData);
	const binaryString = Array.from(uint8Array).map(byte=>String.fromCharCode(byte)).join("");
	const base64Data = btoa(binaryString);

	await nativeBridge.Call("SaveAudioDataToFile", {
		base64Data,
		relFilePath,
	});
}

// high-level
// ==========

/*export const magicTag_soundQuiz = "[sound quiz]";
export const magicTagPrefix_soundPath = "[sound:";
export const GetSoundPathFromTag = (tag: string)=>/\[sound:(.+?)\]/.exec(tag)?.[1];
export const GetDurationLimitFromTag = (tag: string)=>/\[length:(\d+)\]/.exec(tag)?.[1]?.ToFloat();
export const GetLoopFromTag = (tag: string)=>/\[loop:([01])\]/.exec(tag)?.[1]?.ToInt() == 1;
export const GetRunCountFromTag = (tag: string)=>/\[runCount:(\d+)\]/.exec(tag)?.[1]?.ToInt();
export const GetWordsIgnoreGroupFromTag = (tag: string)=>/\[wordsIgnore:(\d+)\]/.exec(tag)?.[1]?.ToInt();

export function IsSoundEffectTagForMediaPlayer(tag: string|n) {
	return tag == magicTag_soundQuiz || (tag != null && GetSoundPathFromTag(tag) != null);
}

export async function PlaySoundEffectTagForMediaPlayer(tag: string|n, intensity: number, session: FBASession) {
	if (tag == magicTag_soundQuiz) {
		let soundStartTime = Date.now();
		const [playerId, playedAudio_filePath, playedAudio_contentUri] = await PlaySound_ByFolderOrPath(session.c.dreamQuiz.soundFolder, null, intensity, GetDurationLimitFromTag(tag) ?? session.c.dreamQuiz.soundDurationLimit, GetLoopFromTag(tag) ?? true);
		SessionLog(`Played audio file: ${playedAudio_filePath}`);
		Android_VLog(VLogLevel.INFO, `Played audio file: ${playedAudio_filePath}`);

		// registering utterance just after sound-end is *usually* fine (due to transcribe-chunking delay), but proper solution wanted eventually 
		NotifyNonTTSUtteranceRange_FromSoundFile(playedAudio_filePath, GetWordsIgnoreGroupFromTag(tag), soundStartTime);
	} else if (tag && GetSoundPathFromTag(tag)) {
		const runCount = GetRunCountFromTag(tag) ?? 1;

		let canceled = false;
		const listener = ()=>canceled = true;
		PlaySound_StopAll_extraListeners.push(listener);

		for (let i = 0; i < runCount; i++) {
			let soundStartTime = Date.now();
			const [playerId, playedAudio_filePath, playedAudio_contentUri] = await PlaySound_ByFolderOrPath(GetSoundPathFromTag(tag)!, null, intensity, GetDurationLimitFromTag(tag) ?? session.c.dreamQuiz.soundDurationLimit, GetLoopFromTag(tag) ?? true);
			SessionLog(`Played audio file: ${playedAudio_filePath} @runIndex:${i}`);
			Android_VLog(VLogLevel.INFO, `Played audio file: ${playedAudio_filePath} @runIndex:${i}`);

			// registering utterance just after sound-end is *usually* fine (due to transcribe-chunking delay), but proper solution wanted eventually 
			NotifyNonTTSUtteranceRange_FromSoundFile(playedAudio_filePath, GetWordsIgnoreGroupFromTag(tag), soundStartTime);

			if (canceled) break;
		}
		
		PlaySound_StopAll_extraListeners.Remove(listener);
	} else {
		AssertNotify("Sound-effect tag is not for media-player!");
	}
}*/